// The live game: owns the canvas loop, input and animation. Who is across the
// board (bot, a second person here, a remote player) is the mode's business.
import { announce } from '@bn-games/ui-shell';
import { playGameOver, playPiecePlace } from '@bn-games/audio-core';
import { t, translateDynamic } from '../i18n/I18n.js';
import { BotPlayer } from '../ai/BotPlayer.js';
import { isInCheck } from '../game/attacks.js';
import { themeToRenderPalette } from '../render/palette.js';
import type { ViewerRotation } from '../render/geometry.js';
import { THEMES } from '../settings/themes.js';
import type { SettingsManager } from '../settings/SettingsManager.js';
import type { GameState } from '../game/GameState.js';
import type { LocalOpponent } from '../ui/types.js';
import { narrateMove } from '../a11y/narration.js';
import { BoardInteraction } from './BoardInteraction.js';
import { resultTitle, terminationText } from './GameOverHost.js';
import { createLocalMode } from './LocalMode.js';
import type { MatchMove } from './Match.js';
import { createOnlineMode, type ChessRoom } from './OnlineMode.js';
import { RafLoop } from './RafLoop.js';
import { SceneChrome } from './SceneChrome.js';
import { SceneAnimation } from './SceneAnimation.js';
import { renderFrame, type FrameIcons } from './SceneRender.js';
import type { ModeHooks, RestartState, SceneMode } from './SceneMode.js';
import { TurnCues } from './TurnCues.js';
import { buildFrameModel } from './frameModel.js';

export type MatchSetup =
  | { readonly kind: 'local'; readonly opponent: LocalOpponent; readonly start?: GameState }
  | { readonly kind: 'online'; readonly room: ChessRoom; readonly onLeave: () => void };

export interface GameSceneDeps {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly settings: SettingsManager;
  readonly openPause: () => void;
  readonly exitToMenu: () => void;
  /** Online: the restart handshake moved, so the pause menu should re-read it. */
  readonly onRestartStateChange: () => void;
}

export class GameScene {
  private readonly bot = new BotPlayer();
  private readonly loop = new RafLoop(() => this.tick());
  private readonly animation = new SceneAnimation();
  private readonly cues: TurnCues;
  private setup: MatchSetup | null = null;
  private mode: SceneMode | null = null;
  private interaction: BoardInteraction | null = null;
  private chrome: SceneChrome | null = null;
  private icons: FrameIcons | null = null;
  /** Game-over sound + narration fired for this game. */
  private endAnnounced = false;
  /** Game-over modal opened for this game (after the last move's animation). */
  private endModalShown = false;

  constructor(private readonly deps: GameSceneDeps) {
    this.cues = new TurnCues(deps.settings, () => this.mode?.match ?? null);
  }

  mount(setup: MatchSetup): void {
    this.unmount();
    this.setup = setup;
    const { settings, canvas } = this.deps;
    const hooks: ModeHooks = {
      onMove: (played) => this.onMove(played),
      onChange: () => this.sync(),
      onNewGame: () => this.resetGameFlags(),
    };
    const mode = setup.kind === 'local'
      ? createLocalMode({ opponent: setup.opponent, start: setup.start ?? null, settings, bot: this.bot, hooks, playAgain: () => this.restart(), exitToMenu: this.deps.exitToMenu })
      : createOnlineMode({ room: setup.room, settings, hooks, onLeave: setup.onLeave, onRestartStateChange: this.deps.onRestartStateChange });
    this.mode = mode;
    this.interaction = new BoardInteraction({
      canvas,
      getRotation: () => this.rotation(),
      getIcons: () => this.icons,
      onGear: () => this.deps.openPause(),
      moveList: { shown: () => settings.get().showMoveList, toggle: () => settings.update({ showMoveList: !settings.get().showMoveList }) },
      onMove: (move) => mode.match.playHuman(move),
      onActivity: () => this.cues.poke(),
      translate: translateDynamic,
    });
    this.chrome = new SceneChrome(canvas, mode, settings);
    this.resetGameFlags();
    this.sync();
    this.loop.start();
    mode.match.start();
    canvas.focus();
  }

  unmount(): void {
    this.loop.stop();
    this.cues.stop();
    this.mode?.dispose();
    this.interaction?.dispose();
    this.chrome?.dispose();
    this.mode = null;
    this.interaction = null;
    this.chrome = null;
    this.animation.clear();
  }

  /** Local: remount, so changed settings apply. Online: propose a restart to the opponent. */
  restart(): void {
    if (this.mode === null || this.setup === null) return;
    if (this.setup.kind === 'local') this.mount(this.setup);
    else this.mode.requestRestart();
  }

  /** Language changed mid-game: refresh the DOM text the scene owns. */
  relabel(): void {
    this.chrome?.relabel();
  }

  resign(): void {
    this.mode?.match.resign();
  }

  /** The pause menu's draw offer: its label and whether it may be made now; null with no game on. */
  drawOffer(): { readonly label: string; readonly enabled: boolean; readonly offer: () => void } | null {
    const mode = this.mode;
    return mode === null ? null : { ...mode.drawButton(), offer: () => mode.offerDraw() };
  }

  isOnline(): boolean {
    return this.mode?.online === true;
  }

  /** An online game is being played right now. */
  isOnlineGameLive(): boolean {
    return this.isOnline() && this.mode?.match.state.isOver === false;
  }

  restartState(): RestartState {
    return this.mode?.restartState() ?? { pending: false, canPropose: true };
  }

  private resetGameFlags(): void {
    this.cues.reset();
    this.animation.clear();
    this.endAnnounced = false;
    this.endModalShown = false;
  }

  private onMove(played: MatchMove): void {
    const mode = this.mode;
    if (mode === null) return;
    playPiecePlace();
    this.cues.moved();
    this.animation.start(played, performance.now());
    const after = mode.match.state.position;
    announce(narrateMove(translateDynamic, played.move, isInCheck(after.board, after.turn)));
    this.sync();
  }

  /** Push the match state into input, sounds, AFK, and the DOM around the canvas. */
  private sync(): void {
    const mode = this.mode;
    if (mode === null) return;
    const state = mode.match.state;
    const canMove = mode.match.canHumanMove();
    this.interaction?.setPosition(state.position.board, state.turn, canMove ? state.legalMoves : []);
    this.cues.sync(canMove);
    this.chrome?.update();
    if (state.outcome !== null && !this.endAnnounced) {
      this.endAnnounced = true;
      playGameOver();
      const view = { outcome: state.outcome, perspective: mode.perspective() };
      announce(t('narration.gameOver', { result: resultTitle(view), reason: terminationText(state.outcome.termination) }));
    }
  }

  private rotation(): ViewerRotation {
    const mode = this.mode;
    return mode === null ? 0 : mode.rotation(this.animation.turnBefore ?? mode.match.state.turn);
  }

  private tick(): void {
    const mode = this.mode;
    const interaction = this.interaction;
    if (mode === null || interaction === null) return;
    const now = performance.now();
    mode.tick(document.querySelector('.modal-overlay') !== null);
    const animation = this.animation.frame(now, this.rotation());
    const s = this.deps.settings.get();
    const model = buildFrameModel({
      match: mode.match, view: interaction.view(), rotation: this.rotation(), perspective: mode.perspective(),
      banner: mode.banner(), showValidMoves: s.showValidMoves, moveListShown: s.showMoveList, animation, hint: this.cues.hint(), timeMs: now,
    });
    this.icons = renderFrame(this.deps.ctx, model, themeToRenderPalette(THEMES[s.theme]));
    this.chrome?.frame();
    const outcome = mode.match.state.outcome;
    if (outcome !== null && !this.animation.active && !this.endModalShown) {
      this.endModalShown = true;
      mode.showGameOver(outcome);
    }
  }
}
